<template>
  <div
    :class="['patient-row', { 'patient-row--overdue': isOverdueLocal, 'patient-row--expanded': isExpanded }]"
    :style="{ borderLeftColor: borderColor }"
  >
    <div
      class="patient-row__content"
      role="button"
      tabindex="0"
      @click="handleExpand"
      @keydown.enter="handleExpand"
      @keydown.space.prevent="handleExpand"
    >
      <div
        class="patient-row__column patient-row__column--reason"
        :class="{ 'patient-row__column--center': isOtherCategory }"
      >
        <div v-if="isOtherCategory" class="patient-row__other-indicator">--</div>
        <div
          v-else
          class="patient-row__status-badge"
          :style="{ backgroundColor: statusBadgeColor, color: statusBadgeTextColor }"
        >
          {{ displayReason }}
        </div>
      </div>

      <div class="patient-row__column patient-row__column--status">
        <div class="patient-row__status-details">
          <div v-if="patient.dischargeOrderTime" class="patient-row__discharge-time">
            {{ dischargeOrderLabel }}: {{ formatDischargeTime }}
          </div>
          <div v-if="patient.status === 'new-admit' && patient.admissionDateTime" class="patient-row__discharge-time">
            Admitted: {{ formatAdmissionTime }}
          </div>
          <div class="patient-row__status-row">
            <div v-if="isOverdueLocal" class="patient-row__overdue">
              <v-icon size="16" color="#D73D17">mdi-alert-circle</v-icon>
              <span class="patient-row__overdue-text">{{ formatOverdueTime }} overdue</span>
            </div>
            <span v-if="isOverdueLocal" class="patient-row__separator">|</span>
            <div class="patient-row__call-log" :class="`patient-row__call-log--${callLogDisplay.type}`">
              {{ callLogDisplay.text }}
            </div>
            <div v-if="hasAvailabilityResponse" class="patient-row__availability-badge" :class="`patient-row__availability-badge--${availabilityResponseType}`">
              <v-icon size="14" :color="availabilityBadgeColor">{{ availabilityBadgeIcon }}</v-icon>
              <span>{{ availabilityBadgeText }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="patient-row__column patient-row__column--diagnosis">
        <div class="patient-row__diagnosis-text">
          {{ patient.primaryDiagnosis }}
        </div>
      </div>

      <div class="patient-row__column patient-row__column--room">
        <div class="patient-row__room-info">
          <v-icon size="16" color="#D9D9D9">mdi-hospital-box</v-icon>
          <span class="patient-row__room-text">
            {{ patient.room.roomNumber }} / Bed {{ patient.bed.bedNumber }} / {{ patient.patientName }}
          </span>
        </div>
      </div>

      <div class="patient-row__actions">
        <v-btn class="patient-row__call-btn" variant="flat" size="small" rounded="pill" @click.stop="handleCall">
          Call
        </v-btn>
        <v-btn
          class="patient-row__breakthrough-btn"
          variant="outlined"
          size="small"
          rounded="pill"
          @click.stop="handleBreakthrough"
        >
          Breakthrough
        </v-btn>
        <v-btn icon variant="text" size="small" class="patient-row__expand-btn" @click.stop="handleExpand">
          <template #default>
            <v-icon>{{ isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
          </template>
        </v-btn>
      </div>
    </div>

    <v-expand-transition>
      <div v-show="isExpanded" class="patient-row__expanded">
        <div class="patient-row__expanded-content">
          <div class="patient-row__details">
            <div class="patient-row__detail-item">
              <span class="patient-row__detail-label">Admitted</span>
              <span class="patient-row__detail-value">{{ formatAdmittedDate }}</span>
            </div>
            <div class="patient-row__detail-item">
              <span class="patient-row__detail-label">Discharge Order</span>
              <span class="patient-row__detail-value">{{ formatDischargeOrderDate }}</span>
            </div>
            <div class="patient-row__detail-item">
              <span class="patient-row__detail-label">Bedside Nurse</span>
              <span class="patient-row__detail-value">{{ bedsideNurseName || "Not assigned" }}</span>
            </div>
            <div class="patient-row__detail-item">
              <span class="patient-row__detail-label">DOB</span>
              <span class="patient-row__detail-value">{{ formatDateOfBirth }}</span>
            </div>
            <div class="patient-row__detail-item patient-row__detail-item--gender">
              <span class="patient-row__detail-label">Gender</span>
              <div class="patient-row__gender">
                <div class="patient-row__gender-badge" :style="{ backgroundColor: genderColor }"></div>
                <span class="patient-row__detail-value">{{ patient.gender || "M" }}</span>
              </div>
            </div>
          </div>

          <div class="patient-row__risks">
            <div v-if="patient.patientFlags.length > 0" class="patient-row__risk-badges">
              <div
                v-for="(flag, index) in patient.patientFlags"
                :key="index"
                class="patient-row__risk-badge"
                :class="getRiskBadgeClass(flag)"
              >
                <v-icon size="24" color="#291758">{{ getRiskIcon(flag) }}</v-icon>
                <span>{{ flag }}</span>
              </div>
            </div>
            <v-btn icon variant="text" size="small" class="patient-row__chart-btn">
              <v-icon size="24" color="#222222">mdi-clipboard-pulse-outline</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="patient-row__call-history-section">
          <div class="patient-row__call-history-title">CALL HISTORY</div>
          <VSkeletonParagraph v-if="callHistoryLoading" class="patient-row__call-history-skeleton" />
          <div v-else-if="callHistory.length === 0" class="patient-row__call-history-empty">
            No call history
          </div>
          <div v-else class="patient-row__call-history-list">
            <div
              v-for="log in callHistory"
              :key="log.callId"
              class="patient-row__call-history-item"
            >
              <div class="patient-row__call-history-main">
                <v-chip
                  size="x-small"
                  :style="{ 
                    backgroundColor: getCallHistoryOutcomeColor(log) + ' !important', 
                    color: '#ffffff !important',
                    opacity: '1 !important'
                  }"
                  class="patient-row__call-history-tag"
                >{{ getCallHistoryOutcomeLabel(log) }}</v-chip>
                
                <!-- Breakthrough indicator -->
                <v-chip
                  v-if="log.isBreakthrough"
                  size="x-small"
                  :style="{ 
                    backgroundColor: '#D79D17 !important', 
                    color: '#ffffff !important',
                    opacity: '1 !important'
                  }"
                  class="patient-row__breakthrough-chip"
                >
                  <v-icon start size="12">mdi-alert-circle</v-icon>
                  Breakthrough
                  <v-tooltip activator="parent" location="top" v-if="log.breakthroughReason">
                    {{ log.breakthroughReason }}
                  </v-tooltip>
                </v-chip>
                
                <div v-if="log.initiatorName" class="patient-row__call-history-nurse">{{ log.initiatorName }}</div>
                
                <div class="patient-row__call-history-time">{{ formatTimeAgo(log.updatedAt) }}</div>
                <div v-if="log.duration" class="patient-row__call-history-duration">
                  {{ formatDuration(log.duration) }}
                </div>
              </div>
            
              <!-- Notes display (full width, separate row) -->
              <div v-if="log.notes" class="patient-row__call-history-notes">
                <v-icon size="14" color="#6A5D8A">mdi-note-text</v-icon>
                <span class="patient-row__notes-text">{{ log.notes }}</span>
                
                <!-- Show edit button if within 15-minute window -->
                <v-btn
                  v-if="canEditNotes(log)"
                  icon
                  size="x-small"
                  variant="text"
                  @click="handleEditNotes(log)"
                >
                  <v-icon size="14">mdi-pencil</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-expand-transition>
    
    <!-- Edit notes modal -->
    <notes-modal
      v-model="showEditNotesModal"
      :call-was-accepted="true"
      :session-id="editingCallLog?.sessionId || ''"
      :existing-notes="editingCallLog?.notes || ''"
      :patient-name="patient.patientName"
      @notes-saved="handleEditNotesSaved"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import type { Patient, CallLog } from "@virtual-care/shared";
import { formatTimeAgo, formatOverdueTime as formatOverdueTimeUtil, formatElapsedTime } from "../utils/timeFormat";
import { callLogService } from "../services/callLogService";
import { usePatientStore } from "../store/patientStore";
import NotesModal from "./NotesModal.vue";

interface Props {
  patient: Patient;
}

interface Emits {
  (e: "call", patientId: string): void;
  (e: "breakthrough", patientId: string): void;
  (e: "expand", patientId: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const patientStore = usePatientStore();

const isExpanded = ref(false);
const callHistory = ref<CallLog[]>([]);
const callHistoryLoading = ref(false);

// Edit notes modal state
const showEditNotesModal = ref(false);
const editingCallLog = ref<CallLog | null>(null);
const userId = "nurse-001"; // TODO: Get from auth context

watch(isExpanded, async (expanded) => {
  if (!expanded || callHistory.value.length > 0) return;
  callHistoryLoading.value = true;
  try {
    callHistory.value = await callLogService.getCallLogsByPatient(props.patient.patientId, 10);
  } catch {
    callHistory.value = [];
  } finally {
    callHistoryLoading.value = false;
  }
});

const getCallHistoryOutcomeColor = (log: CallLog): string => {
  if (log.status === "initiated" || log.status === "accepted") return "#795de9";
  if (log.endReason === "normal") return "#25883b";
  if (log.endReason === "declined") return "#d73d17";
  return "#7f8287";
};

const getCallHistoryOutcomeLabel = (log: CallLog): string => {
  if (log.status === "initiated") return "Calling...";
  if (log.status === "accepted") return "In progress";
  if (log.endReason === "cancelled") return "Cancelled";
  if (log.endReason === "timeout" || log.status === "failed") return "No answer";
  if (log.endReason === "declined") return "Declined";
  if (log.endReason === "normal") return "Completed";
  return "Unknown";
};

const formatDuration = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

// Notes editing functions
const canEditNotes = (log: CallLog): boolean => {
  if (!log.notesEditedAt) return false;
  const editedAt = new Date(log.notesEditedAt);
  const now = new Date();
  const minutesSince = (now.getTime() - editedAt.getTime()) / (1000 * 60);
  return minutesSince <= 15;
};

const handleEditNotes = (log: CallLog) => {
  // Reuse the same NotesModal component for editing
  editingCallLog.value = log;
  showEditNotesModal.value = true;
};

const handleEditNotesSaved = async (notes: string) => {
  if (!editingCallLog.value) return;
  
  try {
    await callLogService.updateCallLog(editingCallLog.value.callId, {
      notes,
      notesEditedAt: new Date().toISOString(),
      notesEditedBy: userId,
    });
    
    showEditNotesModal.value = false;
    editingCallLog.value = null;
    
    // Refresh call history to show updated notes
    callHistoryLoading.value = true;
    callHistory.value = await callLogService.getCallLogsByPatient(props.patient.patientId, 10);
    
    // Also refresh the patient's recent call log in the store
    await patientStore.refreshPatientCallLog(props.patient.patientId);
  } catch (err) {
    console.error('[PatientRow] Failed to update notes:', err);
  } finally {
    callHistoryLoading.value = false;
  }
};

const OVERDUE_THRESHOLD_HOURS = 6;
const PRIMARY_CATEGORIES = ["discharge-ready", "condition-discharge", "new-admit"];

const isOtherCategory = computed(() => {
  return !PRIMARY_CATEGORIES.includes(props.patient.category);
});

const isOverdueLocal = computed(() => {
  const now = Date.now();
  const recentLog = props.patient.recentCallLog;
  
  // For Discharge Ready and Conditional Discharge patients
  if (props.patient.dischargeOrderTime) {
    const orderTime = new Date(props.patient.dischargeOrderTime).getTime();
    const hoursSinceOrder = (now - orderTime) / (1000 * 60 * 60);
    
    // Not overdue if less than threshold
    if (hoursSinceOrder <= OVERDUE_THRESHOLD_HOURS) return false;
    
    // Check if there's been a successful completed call since discharge order
    if (recentLog && recentLog.status === 'completed') {
      const callTime = new Date(recentLog.endTime || recentLog.startTime).getTime();
      // If the completed call happened AFTER the discharge order, patient is no longer overdue
      if (callTime > orderTime) {
        return false;
      }
    }
    
    return true;
  }
  
  // For New Admit patients - check admission time
  if (props.patient.status === 'new-admit' && props.patient.admissionDateTime) {
    const admissionTime = new Date(props.patient.admissionDateTime).getTime();
    const hoursSinceAdmission = (now - admissionTime) / (1000 * 60 * 60);
    
    // Not overdue if less than threshold
    if (hoursSinceAdmission <= OVERDUE_THRESHOLD_HOURS) return false;
    
    // Check if there's been a successful completed call since admission
    if (recentLog && recentLog.status === 'completed') {
      const callTime = new Date(recentLog.endTime || recentLog.startTime).getTime();
      // If the completed call happened AFTER admission, patient is no longer overdue
      if (callTime > admissionTime) {
        return false;
      }
    }
    
    return true;
  }
  
  return false;
});

const borderColor = computed(() => {
  if (isOverdueLocal.value) {
    return "#D73D17";
  }
  return "transparent";
});

const statusBadgeColor = computed(() => {
  if (props.patient.status === "discharge-ready") {
    return isOverdueLocal.value ? "#D73D17" : "#EEF5EF";
  } else if (props.patient.status === "condition-discharge") {
    return isOverdueLocal.value ? "#D73D17" : "#FEF3E8";
  } else if (props.patient.status === "new-admit") {
    return isOverdueLocal.value ? "#D73D17" : "#E8EEFB";
  } else if (isOtherCategory.value) {
    return "#F5F5F5";
  }
  return "#D73D17";
});

const statusBadgeTextColor = computed(() => {
  if (props.patient.status === "discharge-ready") {
    return isOverdueLocal.value ? "#FFFFFF" : "#222222";
  } else if (props.patient.status === "condition-discharge") {
    return isOverdueLocal.value ? "#FFFFFF" : "#222222";
  } else if (props.patient.status === "new-admit") {
    return isOverdueLocal.value ? "#FFFFFF" : "#222222";
  } else if (isOtherCategory.value) {
    return "#222222";
  }
  return "#FFFFFF";
});

const bedsideNurseName = computed(() => {
  const bedsideNurse = props.patient.careTeamMembers.find((member) => member.isBedside);
  return bedsideNurse?.name || null;
});

const dischargeOrderLabel = computed(() => {
  return props.patient.status === "condition-discharge" ? "Conditional trigger" : "Discharge order";
});

const displayReason = computed(() => {
  return props.patient.status === "condition-discharge" ? "Conditional Discharge" : props.patient.reason;
});

const formatDischargeTime = computed(() => {
  return formatElapsedTime(props.patient.dischargeOrderTime);
});

const formatAdmissionTime = computed(() => {
  return formatElapsedTime(props.patient.admissionDateTime);
});

const formatOverdueTime = computed(() => {
  // For discharge ready patients, use discharge order time
  if (props.patient.dischargeOrderTime) {
    return formatOverdueTimeUtil(props.patient.dischargeOrderTime);
  }
  // For new admit patients, use admission time
  if (props.patient.status === 'new-admit' && props.patient.admissionDateTime) {
    return formatOverdueTimeUtil(props.patient.admissionDateTime);
  }
  return "";
});

const callLogDisplay = computed(() => {
  const recentLog = props.patient.recentCallLog;
  const totalCount = props.patient.totalCallCount || 0;

  // Rule 1: No call log present
  if (!recentLog) {
    const thresholdMinutes = OVERDUE_THRESHOLD_HOURS * 60;
    return {
      text: `Target response within ${thresholdMinutes} minutes`,
      type: "default",
    };
  }

  // Rule 4: Call in progress (patient answered)
  if (recentLog.status === "accepted") {
    return {
      text: "In progress…",
      type: "in-progress",
    };
  }

  // Rule 5: Call initiated but not yet answered
  if (recentLog.status === "initiated") {
    return {
      text: "Calling…",
      type: "calling",
    };
  }

  const timeAgo = formatTimeAgo(recentLog.updatedAt);

  // Rule 2: Cancelled by nurse
  if (recentLog.endReason === "cancelled") {
    return {
      text: `Cancelled • ${timeAgo}`,
      type: "cancelled",
    };
  }

  // Rule 3: Not answered or timeout
  if (recentLog.endReason === "timeout" || recentLog.status === "failed") {
    return {
      text: `Attempt ${totalCount} • ${timeAgo} • No answer`,
      type: "no-answer",
    };
  }

  // Rule 4: Declined
  if (recentLog.endReason === "declined") {
    return {
      text: `Declined call • ${timeAgo}`,
      type: "declined",
    };
  }

  // Rule 5: Normal completion
  if (recentLog.endReason === "normal") {
    return {
      text: `Completed • ${timeAgo}`,
      type: "completed",
    };
  }

  // Fallback
  return {
    text: `Last call • ${timeAgo}`,
    type: "default",
  };
});

// Availability response badge
const hasAvailabilityResponse = computed(() => {
  const hasResponse = !!props.patient.availabilityResponse;
  if (hasResponse) {
    console.log('[PatientRow] Patient has availability response:', props.patient.patientId, props.patient.availabilityResponse);
  }
  return hasResponse;
});

const availabilityResponseType = computed(() => {
  return props.patient.availabilityResponse?.response || 'not_now';
});

const availabilityBadgeIcon = computed(() => {
  return props.patient.availabilityResponse?.response === 'available' 
    ? 'mdi-check-circle' 
    : 'mdi-clock-outline';
});

const availabilityBadgeColor = computed(() => {
  return props.patient.availabilityResponse?.response === 'available' 
    ? '#25883B' 
    : '#D79D17';
});

const availabilityBadgeText = computed(() => {
  return props.patient.availabilityResponse?.response === 'available' 
    ? 'Available now' 
    : 'Will call back';
});

const formatAdmittedDate = computed(() => {
  if (!props.patient.admissionDateTime) return "N/A";
  const date = new Date(props.patient.admissionDateTime);
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear();
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${day}-${month}-${year}, ${time}`;
});

const formatDischargeOrderDate = computed(() => {
  if (!props.patient.dischargeOrderTime) return "N/A";
  const date = new Date(props.patient.dischargeOrderTime);
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear();
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${day}-${month}-${year} ${time}`;
});

const formatDateOfBirth = computed(() => {
  if (!props.patient.dateOfBirth) return "N/A";
  const dob = new Date(props.patient.dateOfBirth);
  const now = new Date();

  const ageYears = now.getFullYear() - dob.getFullYear();
  const ageMonths = now.getMonth() - dob.getMonth();

  const day = dob.getDate();
  const month = dob.toLocaleDateString("en-US", { month: "short" });
  const year = dob.getFullYear();

  return `${day}-${month}-${year} (${ageYears}y ${Math.abs(ageMonths)}m)`;
});

const getRiskIcon = (flag: string): string => {
  const flagLower = flag.toLowerCase();
  if (flagLower.includes("fall")) return "mdi-human-cane";
  if (flagLower.includes("isolation")) return "mdi-biohazard";
  if (flagLower.includes("safety")) return "mdi-alert";
  if (flagLower.includes("elopement")) return "mdi-exit-run";
  if (flagLower.includes("seizure")) return "mdi-brain";
  if (flagLower.includes("aspiration") || flagLower.includes("npo")) return "mdi-cup-off";
  if (flagLower.includes("allergy") || flagLower.includes("latex")) return "mdi-allergy";
  if (flagLower.includes("contact") || flagLower.includes("droplet") || flagLower.includes("airborne"))
    return "mdi-shield-alert";
  return "mdi-information";
};

const getRiskBadgeClass = (flag: string): string => {
  const flagLower = flag.toLowerCase();
  if (flagLower.includes("fall")) return "patient-row__risk-badge--fall";
  if (flagLower.includes("isolation")) return "patient-row__risk-badge--isolation";
  if (flagLower.includes("safety")) return "patient-row__risk-badge--safety";
  return "";
};

const genderColor = computed(() => {
  const gender = props.patient.gender || "M";
  return gender === "M" ? "#5DA7B5" : "#BC7AA9";
});

const handleCall = () => {
  emit("call", props.patient.patientId);
};

const handleBreakthrough = () => {
  emit("breakthrough", props.patient.patientId);
};

const handleExpand = () => {
  isExpanded.value = !isExpanded.value;
  emit("expand", props.patient.patientId);
};
</script>

<style lang="scss" scoped>
.patient-row {
  position: relative;
  border-left: 4px solid transparent;
  transition: all 0.2s ease;

  &--overdue {
    border-left-color: #d73d17;
  }
}

.patient-row__content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0 12px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: transparent;
  cursor: pointer;
}

.patient-row--overdue .patient-row__content {
  background: linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, rgba(255, 248, 247, 1) 14%, rgba(255, 255, 255, 1) 70%);
}

.patient-row--overdue .patient-row__expanded-content {
  background: linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, rgba(255, 248, 247, 1) 14%, rgba(255, 255, 255, 1) 70%);
  box-shadow: 0px 1px 2px 0px rgba(19, 19, 19, 0.08), 0px 2px 3px 0px rgba(19, 19, 19, 0.06) !important;
}

.patient-row--overdue .patient-row__expanded {
  background: linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, rgba(255, 248, 247, 1) 14%, rgba(255, 255, 255, 1) 70%);
}

.patient-row--expanded:not(.patient-row--overdue) {
  border-left-color: rgb(106, 93, 138) !important;
}

.patient-row--expanded .patient-row__content {
  border-bottom: none;
}

.patient-row--expanded:not(.patient-row--overdue) .patient-row__content {
  background: #f6f5fb;
}

.patient-row--expanded:not(.patient-row--overdue) .patient-row__expanded-content {
  background: #f6f5fb;
}

.patient-row__column {
  display: flex;
  align-items: center;
  align-self: stretch;
  padding: 0 12px 0 0;
  border-right: 1px solid #d4d1de;

  &--reason {
    flex: 0 1 22%;
    min-width: 220px;
  }

  &--center {
    justify-content: center;
  }

  &--status {
    flex: 0 1 32%;
    min-width: 320px;
  }

  &--diagnosis {
    flex: 0 1 24%;
    min-width: 240px;
  }

  &--room {
    flex: 0 1 22%;
    min-width: 220px;
  }

  @media (max-width: 959px) {
    &--diagnosis {
      display: none;
    }
  }

  @media (max-width: 599px) {
    &--status,
    &--diagnosis {
      display: none;
    }
  }
}

.patient-row__status-badge {
  padding: 1.5px 8px;
  border-radius: 4px;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.5em;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.patient-row__other-indicator {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 1.5em;
  color: #9e9e9e;
}

.patient-row__status-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.patient-row__discharge-time {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.428em;
  color: #000000de;
}

.patient-row__status-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.patient-row__separator {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.333em;
  color: #d9d9d9;
}

.patient-row__overdue {
  display: flex;
  align-items: center;
  gap: 4px;
}

.patient-row__overdue-text {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.333em;
  color: #d73d17;
}

.patient-row__call-log {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.333em;
  white-space: nowrap;

  &--default {
    color: #00000099;
  }

  &--in-progress {
    color: #795de9;
    font-weight: 600;
  }

  &--calling {
    color: #1976d2;
    font-weight: 500;
  }

  &--cancelled {
    color: #757575;
    font-weight: 400;
  }

  &--no-answer {
    color: #d73d17;
  }

  &--declined {
    color: #ff9800;
  }

  &--completed {
    color: #4caf50;
  }
}

.patient-row__availability-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 500;
  font-size: 11px;
  line-height: 1.2em;
  white-space: nowrap;

  &--available {
    background-color: #eef5ef;
    color: #25883b;
  }

  &--not_now {
    background-color: #fef3e8;
    color: #d79d17;
  }
}

.patient-row__diagnosis-text {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.428em;
  color: #000000de;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

.patient-row__room-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.patient-row__room-text {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.428em;
  color: #000000de;
}

.patient-row__actions {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  gap: 8px;
  flex: 0 1 22%;
}

.patient-row__call-btn {
  background-color: var(--accent, #795de9) !important;
  text-transform: none;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0;
  color: #ffffff !important;
  padding: 16px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  @media (max-width: 599px) {
    min-width: auto;
    padding: 16px 12px !important;
  }
}

.patient-row__breakthrough-btn {
  text-transform: none;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0;
  border-color: rgba(0, 0, 0, 0.23);
  color: #000000de;
  padding: 16px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  @media (max-width: 767px) {
    display: none;
  }
}

.patient-row__expand-btn {
  :deep(.v-btn__content) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.patient-row__expanded {
  background: #f6f5fb;
  margin-bottom: 3px;
}

.patient-row--overdue .patient-row__expanded {
  background: linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, rgba(255, 248, 247, 1) 14%, rgba(255, 255, 255, 1) 70%);
}

.patient-row__expanded-content {
  background: #ffffff;
  padding: 20px 12px 20px 24px;
  border-radius: 4px;
  box-shadow: 0px 1px 2px 0px rgba(19, 19, 19, 0.08), 0px 2px 3px 0px rgba(19, 19, 19, 0.06);
}

.patient-row__details {
  display: flex;
  gap: 24px;
  margin-bottom: 10px;
}

.patient-row__detail-item {
  display: flex;
  align-items: center;
  gap: 4px;

  &--gender {
    gap: 8px;
  }
}

.patient-row__detail-label {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.15em;
  letter-spacing: 0.4%;
  color: #6a5d8a;
}

.patient-row__detail-value {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.286em;
  color: #222222;
}

.patient-row__gender {
  display: flex;
  align-items: center;
  gap: 4px;
}

.patient-row__gender-badge {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.patient-row__risks {
  display: flex;
  align-items: center;
  gap: 10px;
}

.patient-row__risk-badges {
  display: flex;
  gap: 12px;
  flex: 1;
}

.patient-row__risk-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background: #ffffff;
  border-radius: 4px;
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.5em;
  color: #222222;

  &--safety {
    border-left: 3px solid #ff7b69;
  }

  &--fall {
    border-left: 3px solid #ffe54e;
  }

  &--isolation {
    border-left: 3px solid #8dda80;
  }
}

.patient-row__chart-btn {
  width: 40px;
  height: 40px;
  margin-left: auto;
  background-color: #ffffff !important;
  border-radius: 50% !important;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5 !important;
  }
}

@media (max-width: 767px) {
  .patient-row__details {
    flex-wrap: wrap;
  }

  .patient-row__risks {
    flex-direction: column;
    align-items: flex-start;
  }

  .patient-row__risk-badges {
    flex-wrap: wrap;
  }
}

.patient-row__call-history-section {
  margin-top: 0;
  padding: 12px 16px 0 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.patient-row__call-history-title {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 11px;
  line-height: 1em;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6a5d8a;
  margin-bottom: 10px;
  margin-top: 0;
}

.patient-row__call-history-skeleton {
  max-width: 320px;
}

.patient-row__call-history-empty {
  font-family: "Proxima Nova", sans-serif;
  font-size: 13px;
  color: #999999;
  padding: 0;
}

.patient-row__call-history-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.patient-row__call-history-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: "Proxima Nova", sans-serif;
  font-size: 13px;
  padding: 8px 12px;
  background-color: rgba(106, 93, 138, 0.03);
  border-radius: 6px;
  border-left: 3px solid rgba(106, 93, 138, 0.15);
}

.patient-row__call-history-main {
  display: flex;
  align-items: center;
  gap: 10px;
}

.patient-row__call-history-tag {
  min-width: 80px;
  justify-content: center;
  font-family: "Proxima Nova", sans-serif !important;
  font-size: 11px !important;
  font-weight: 500 !important;
  color: #ffffff !important;
  flex-shrink: 0;
  opacity: 1 !important;
  
  &.v-chip {
    opacity: 1 !important;
  }
  
  ::v-deep(.v-chip__content) {
    color: #ffffff !important;
    font-weight: 500 !important;
    opacity: 1 !important;
  }
  
  ::v-deep(.v-chip__underlay) {
    opacity: 0 !important;
  }
  
  ::v-deep(.v-chip__overlay) {
    opacity: 0 !important;
  }
}

.patient-row__breakthrough-chip {
  font-family: "Proxima Nova", sans-serif !important;
  font-size: 10px !important;
  font-weight: 600 !important;
  color: #ffffff !important;
  height: 22px !important;
  flex-shrink: 0;
  opacity: 1 !important;
  
  ::v-deep(.v-chip__content) {
    color: #ffffff !important;
    opacity: 1 !important;
  }
  
  ::v-deep(.v-chip__underlay) {
    opacity: 0 !important;
  }
  
  ::v-deep(.v-chip__overlay) {
    opacity: 0 !important;
  }
}

.patient-row__call-history-nurse {
  color: #222222;
  font-weight: 500;
}

.patient-row__call-history-time {
  color: #6a5d8a;
  flex: 1;
}

.patient-row__call-history-duration {
  color: #999999;
  font-variant-numeric: tabular-nums;
}

.patient-row__call-history-notes {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  margin-left: 0;
  padding: 8px;
  background: rgba(106, 93, 138, 0.05);
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.4;
  color: #6A5D8A;
  
  .patient-row__notes-text {
    flex: 1;
    word-break: break-word;
  }
}
</style>
